import type {
  IStoreLocation,
  StorefrontOperatingSchedule,
  StorefrontSpecialDate,
} from '../types/storefront.types';

export const WEEKDAY_LABELS: Record<number, string> = {
  0: 'Domingo',
  1: 'Lunes',
  2: 'Martes',
  3: 'Miércoles',
  4: 'Jueves',
  5: 'Viernes',
  6: 'Sábado',
};

export const WEEKDAY_SHORT: Record<number, string> = {
  0: 'Dom',
  1: 'Lun',
  2: 'Mar',
  3: 'Mié',
  4: 'Jue',
  5: 'Vie',
  6: 'Sáb',
};

export const timeToMinutes = (time: string): number => {
  const [hours, minutes] = time.split(':').map(Number);
  return hours * 60 + minutes;
};

const hasDefinedSchedule = (location: IStoreLocation): boolean => {
  const opening = location.openingTime;
  const closing = location.closingTime;
  return Boolean(opening && opening?.trim() && closing && closing?.trim());
};

const hasNewSchedule = (location: IStoreLocation): boolean => {
  return Boolean(location.operatingSchedules && location.operatingSchedules.length > 0);
};

/**
 * Prioridad:
 * 1. Si backend ya computó `isOpen` (server-truth con timezone + SpecialDate + overnight) -> usarlo.
 * 2. Si hay operatingSchedules -> calcular local fallback (multi-slot + overnight) si isOpen no vino.
 * 3. Fallback legacy openingTime/closingTime.
 */
export const isLocationOpen = (location: IStoreLocation): boolean => {
  if (typeof location.isOpen === 'boolean') return location.isOpen;
  if (!location.isOpenForOrders) return false;

  // Si tiene nuevo sistema, intentar calcular localmente (aunque idealmente backend ya mandó isOpen)
  if (hasNewSchedule(location)) {
    return isLocationOpenBySchedules(location);
  }

  if (!hasDefinedSchedule(location)) return true;

  try {
    const now = new Date();
    const timezone = location.timezone || 'America/Caracas';
    const currentTimeInTZ = now.toLocaleTimeString('en-US', {
      timeZone: timezone,
      hour12: false,
      hour: '2-digit',
      minute: '2-digit',
    });
    const openingTime = location.openingTime!;
    const closingTime = location.closingTime!;
    const currentMinutes = timeToMinutes(currentTimeInTZ);
    const openingMinutes = timeToMinutes(openingTime);
    const closingMinutes = timeToMinutes(closingTime);
    if (closingMinutes < openingMinutes) {
      return currentMinutes >= openingMinutes || currentMinutes < closingMinutes;
    }
    return currentMinutes >= openingMinutes && currentMinutes < closingMinutes;
  } catch {
    return location.isOpenForOrders;
  }
};

const isTimeWithin = (time: string, open: string, close: string): boolean => {
  const t = timeToMinutes(time);
  const o = timeToMinutes(open);
  const c = timeToMinutes(close);
  if (o <= c) return t >= o && t < c;
  return t >= o || t < c; // overnight
};

const getCurrentTimeInTZ = (timezone: string): { time: string; day: number; dateStr: string } => {
  const now = new Date();
  const time = now.toLocaleTimeString('en-US', {
    timeZone: timezone,
    hour12: false,
    hour: '2-digit',
    minute: '2-digit',
  });
  const day = Number(
    now.toLocaleDateString('en-US', { timeZone: timezone, weekday: 'short' }) === 'Sun'
      ? 0
      : now.toLocaleString('en-US', { timeZone: timezone, weekday: 'short' })
  );
  // Mejor: usar getDay en timezone vía Intl
  const dowStr = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    weekday: 'short',
  }).format(now);
  const dowMap: Record<string, number> = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 };
  const dow = dowMap[dowStr] ?? now.getDay();
  const dateStr = new Intl.DateTimeFormat('en-CA', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(now); // YYYY-MM-DD
  void day;
  return { time, day: dow, dateStr };
};

const isLocationOpenBySchedules = (location: IStoreLocation): boolean => {
  if (!location.isOpenForOrders) return false;
  const timezone = location.timezone || 'America/Caracas';
  const { time, day, dateStr } = getCurrentTimeInTZ(timezone);

  // SpecialDate tiene prioridad
  if (location.specialDates && location.specialDates.length > 0) {
    const special = location.specialDates.find((s) => {
      const d = s.date.slice(0, 10); // YYYY-MM-DD
      return d === dateStr;
    });
    if (special) {
      if (special.isClosed) return false;
      if (special.openTime && special.closeTime) {
        return isTimeWithin(time, special.openTime, special.closeTime);
      }
      // si no es cerrado y no tiene horas, fallback a semanal
    }
  }

  const schedules = (location.operatingSchedules || [])
    .filter((s) => s.dayOfWeek === day)
    .sort((a, b) => a.sortOrder - b.sortOrder);

  if (schedules.length === 0) return false;
  if (schedules.some((s) => s.isClosed)) {
    // si hay un registro isClosed=true para el día, se considera cerrado (aunque haya otros slots, el backend no debería enviarlos)
    const closedEntry = schedules.find((s) => s.isClosed);
    if (closedEntry && schedules.length === 1) return false;
  }
  return schedules.some((s) => !s.isClosed && isTimeWithin(time, s.openTime, s.closeTime));
};

/**
 * Convierte razones técnicas del backend en mensajes amigables para el cliente.
 * Evita exponer términos como isOpenForOrders, kill-switch o SpecialDate.
 */
export const humanizeScheduleReason = (reason: string | null | undefined): string | null => {
  if (!reason) return null;
  const trimmed = reason.trim();
  if (!trimmed) return null;
  const lower = trimmed.toLowerCase();
  if (
    lower.includes('isopenfororders') ||
    lower.includes('kill-switch') ||
    lower.includes('kill switch')
  ) {
    return 'La sucursal actualmente no está recibiendo pedidos';
  }
  let friendly = trimmed
    .replace(/SpecialDate\s*cerrado:\s*/i, 'Cerrado hoy: ')
    .replace(/SpecialDate\s*/gi, '')
    .replace(/Dentro de slot\s*/i, 'Abierto · ')
    .replace(/\bslot\b/gi, 'horario')
    .replace(/\s{2,}/g, ' ')
    .trim();
  // Limpiar prefijos técnicos residuales
  friendly = friendly.replace(/^isclosed\s*:?\s*/i, '').trim();
  return friendly || null;
};

export const getLocationStatusMessage = (
  location: IStoreLocation
): { isOpen: boolean; message: string } => {
  const isOpen = isLocationOpen(location);

  // 1. Si está pausada manualmente, mensaje prioritario y humano
  if (!location.isOpenForOrders) {
    return { isOpen: false, message: 'La sucursal actualmente no está recibiendo pedidos' };
  }

  // 2. Server reason humanizado
  if (location.scheduleReason) {
    const friendly = humanizeScheduleReason(location.scheduleReason);
    if (friendly) return { isOpen, message: friendly };
  }

  // 3. SpecialDate cerrado hoy
  if (location.specialDates && location.specialDates.length > 0) {
    const timezone = location.timezone || 'America/Caracas';
    const { dateStr } = getCurrentTimeInTZ(timezone);
    const todaySpecial = location.specialDates.find((s) => s.date.slice(0, 10) === dateStr);
    if (todaySpecial?.isClosed) {
      return {
        isOpen: false,
        message: todaySpecial.reason
          ? `Cerrado hoy: ${todaySpecial.reason}`
          : 'Cerrado por feriado',
      };
    }
    if (todaySpecial && !todaySpecial.isClosed && todaySpecial.openTime) {
      return {
        isOpen,
        message: isOpen
          ? `Abierto hoy ${todaySpecial.openTime}-${todaySpecial.closeTime} (${todaySpecial.reason || 'horario especial'})`
          : `Cerrado — hoy ${todaySpecial.openTime}-${todaySpecial.closeTime}`,
      };
    }
  }

  // 4. todayHours del backend
  if (location.todayHours) {
    if (location.todayHours.toLowerCase().includes('cerrado')) {
      return { isOpen: false, message: 'Cerrado hoy' };
    }
    return {
      isOpen,
      message: isOpen
        ? `Abierto · ${location.todayHours}`
        : `Cerrado · Hoy: ${location.todayHours}`,
    };
  }

  // 5. Nuevo sistema: construir todayHours local
  if (hasNewSchedule(location)) {
    const hours = formatTodayHours(location);
    if (hours) {
      return {
        isOpen,
        message: isOpen ? `Abierto · ${hours}` : `Cerrado · Hoy: ${hours}`,
      };
    }
    return { isOpen, message: isOpen ? 'Abierto' : 'Cerrado hoy' };
  }

  // 6. Legacy
  if (!hasDefinedSchedule(location)) {
    return { isOpen: true, message: 'Abierto para pedidos (24/7)' };
  }
  if (isOpen) {
    return { isOpen: true, message: `Abierto hasta las ${location.closingTime || '23:59'}` };
  }
  return { isOpen: false, message: `Cerrado — Abre a las ${location.openingTime || '00:00'}` };
};

export const formatTodayHours = (location: IStoreLocation): string | null => {
  if (location.todayHours) return location.todayHours;
  if (!hasNewSchedule(location)) {
    if (hasDefinedSchedule(location)) return `${location.openingTime}-${location.closingTime}`;
    return null;
  }
  const timezone = location.timezone || 'America/Caracas';
  const { day } = getCurrentTimeInTZ(timezone);
  const todaySchedules = (location.operatingSchedules || [])
    .filter((s) => s.dayOfWeek === day)
    .sort((a, b) => a.sortOrder - b.sortOrder);
  if (todaySchedules.length === 0) return 'Cerrado';
  if (todaySchedules.length === 1 && todaySchedules[0].isClosed) return 'Cerrado';
  return (
    todaySchedules
      .filter((s) => !s.isClosed)
      .map((s) => `${s.openTime}-${s.closeTime}`)
      .join(', ') || 'Cerrado'
  );
};

export const formatWeeklySchedule = (
  schedules: StorefrontOperatingSchedule[] | undefined
): { day: number; label: string; hours: string; isClosed: boolean }[] => {
  const result: { day: number; label: string; hours: string; isClosed: boolean }[] = [];
  for (let d = 0; d < 7; d++) {
    const daySchedules = (schedules || [])
      .filter((s) => s.dayOfWeek === d)
      .sort((a, b) => a.sortOrder - b.sortOrder);
    if (daySchedules.length === 0) {
      result.push({ day: d, label: WEEKDAY_LABELS[d], hours: 'Cerrado', isClosed: true });
    } else if (daySchedules.length === 1 && daySchedules[0].isClosed) {
      result.push({ day: d, label: WEEKDAY_LABELS[d], hours: 'Cerrado', isClosed: true });
    } else {
      const hours = daySchedules
        .filter((s) => !s.isClosed)
        .map((s) => `${s.openTime}-${s.closeTime}`)
        .join(', ');
      result.push({
        day: d,
        label: WEEKDAY_LABELS[d],
        hours: hours || 'Cerrado',
        isClosed: !hours,
      });
    }
  }
  // Ordenar Lun-Dom para vista comercial (1..6,0)
  const ordered = [1, 2, 3, 4, 5, 6, 0].map((d) => result.find((r) => r.day === d)!);
  return ordered;
};

export const getNextSpecialDates = (
  specialDates: StorefrontSpecialDate[] | undefined,
  limit = 5
): StorefrontSpecialDate[] => {
  if (!specialDates || specialDates.length === 0) return [];
  const now = new Date();
  const todayStr = now.toISOString().slice(0, 10);
  return specialDates
    .filter((s) => s.date.slice(0, 10) >= todayStr)
    .sort((a, b) => a.date.localeCompare(b.date))
    .slice(0, limit);
};

export const hasScheduleData = (location: IStoreLocation): boolean => {
  return hasNewSchedule(location) || hasDefinedSchedule(location);
};
